import React, { useEffect, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../App";
import { Plus, Trash2, Download, Printer, X } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const APP_ORIGIN = window.location.origin;

const KIND_LABELS = {
  clock_on: "Clock On (generic)",
  clock_on_job: "Clock On — specific job",
  machine_prestart: "Machine Prestart",
  form: "Form",
  daybook_new: "Daybook (new docket)",
};

const QRCodesPage = () => {
  const { user, currentCompany } = useAuth();
  const isSuper = user?.role === "super_admin";
  const isAdminLike = ["admin", "office_staff", "manager"].includes(user?.role);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [printing, setPrinting] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [machines, setMachines] = useState([]);

  const companyId = currentCompany?.id;

  const fetchTargets = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/companies/${companyId}/qr-targets`);
      setTargets(res.data || []);
    } catch (e) { toast.error("Failed to load QR codes"); }
    finally { setLoading(false); }
  };

  const fetchJobsMachines = async () => {
    try {
      const [jr, mr] = await Promise.all([
        axios.get(`${API}/jobs`).catch(() => ({ data: [] })),
        axios.get(`${API}/machines`).catch(() => ({ data: [] })),
      ]);
      setJobs(jr.data || []);
      setMachines(mr.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchTargets(); fetchJobsMachines(); }, [companyId]);

  if (!isSuper && !isAdminLike) {
    return <div className="text-center py-20 text-slate-400">Admin / office only.</div>;
  }
  if (!companyId) {
    return <div className="text-center py-20 text-slate-400">Open a company first.</div>;
  }

  const onDelete = async (id) => {
    if (!window.confirm("Delete this QR code?")) return;
    try { await axios.delete(`${API}/qr-targets/${id}`); toast.success("Deleted"); fetchTargets(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="qr-codes-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight">QR Codes</h1>
          <p className="text-slate-500 text-sm mt-1">
            Stick these on machines, sites, noticeboards. Workers scan → straight to the right page (auto-login first if needed).
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold flex items-center gap-2" data-testid="new-qr-btn">
          <Plus className="w-5 h-5" />New QR Code
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-400">Loading…</div>
      ) : targets.length === 0 ? (
        <div className="text-center py-10 text-slate-400">No QR codes yet — click "New QR Code".</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {targets.map(t => (
            <QRCard key={t.id} target={t} onDelete={() => onDelete(t.id)} onPrint={() => setPrinting(t)} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateQRModal
          companyId={companyId}
          jobs={jobs}
          machines={machines}
          onClose={() => setShowCreate(false)}
          onCreated={async () => { setShowCreate(false); await fetchTargets(); }}
        />
      )}

      {printing && <PrintQRModal target={printing} onClose={() => setPrinting(null)} />}
    </div>
  );
};

const buildQRUrl = (token) => `${APP_ORIGIN}/qr/${token}`;

const QRCard = ({ target, onDelete, onPrint }) => {
  const url = buildQRUrl(target.token);
  return (
    <div className="bg-white border-2 border-slate-200 rounded-md p-4 flex gap-3" data-testid={`qr-card-${target.id}`}>
      <div className="flex-shrink-0 bg-white p-2 border border-slate-200 rounded">
        <QRCodeSVG value={url} size={96} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-slate-900 truncate" title={target.label}>{target.label}</div>
        <div className="text-xs text-slate-500 mb-1">{KIND_LABELS[target.kind] || target.kind}</div>
        <div className="text-[10px] text-slate-400 mb-2">{target.scan_count} scan(s)</div>
        <div className="flex gap-1">
          <button onClick={onPrint} className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded font-semibold flex items-center gap-1" data-testid={`print-qr-${target.id}`}>
            <Printer className="w-3 h-3" /> Print
          </button>
          <button onClick={onDelete} className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateQRModal = ({ companyId, jobs, machines, onClose, onCreated }) => {
  const [kind, setKind] = useState("clock_on");
  const [label, setLabel] = useState("");
  const [jobId, setJobId] = useState("");
  const [machineId, setMachineId] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const requiresJob = kind === "clock_on_job";
  const requiresMachine = kind === "machine_prestart";

  const submit = async () => {
    if (!label.trim()) { toast.error("Add a label"); return; }
    if (requiresJob && !jobId) { toast.error("Pick a job"); return; }
    if (requiresMachine && !machineId) { toast.error("Pick a machine"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/companies/${companyId}/qr-targets`, {
        kind, label,
        job_id: jobId || null,
        machine_id: machineId || null,
        notes,
      });
      toast.success("QR code created");
      await onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="create-qr-modal">
      <div className="bg-white rounded-md w-full max-w-md shadow-2xl">
        <div className="border-b-2 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold uppercase">New QR Code</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">What's it for?</label>
            <select value={kind} onChange={e => setKind(e.target.value)} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="qr-kind-select">
              {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Label (printed on the sign)</label>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Crane 23 Prestart"
              className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="qr-label" />
          </div>
          {requiresJob && (
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Pick job</label>
              <select value={jobId} onChange={e => setJobId(e.target.value)} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="qr-job-select">
                <option value="">— Select —</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.title || j.name || j.id}</option>)}
              </select>
            </div>
          )}
          {requiresMachine && (
            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Pick machine</label>
              <select value={machineId} onChange={e => setMachineId(e.target.value)} className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="qr-machine-select">
                <option value="">— Select —</option>
                {machines.map(m => <option key={m.id} value={m.id}>{m.name || m.rego || m.id}</option>)}
              </select>
            </div>
          )}
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Optional notes" className="w-full border-2 border-slate-200 rounded px-3 py-2 text-sm" />
        </div>
        <div className="border-t-2 p-4 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
          <button onClick={submit} disabled={saving} className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded font-bold" data-testid="submit-qr-btn">
            {saving ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

const PrintQRModal = ({ target, onClose }) => {
  const url = buildQRUrl(target.token);
  const print = () => window.print();
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="print-qr-modal">
      <div className="bg-white rounded-md p-8 max-w-md w-full" id="qr-print-area">
        <h2 className="text-2xl font-bold text-center mb-2">{target.label}</h2>
        <p className="text-center text-sm text-slate-600 mb-4">{KIND_LABELS[target.kind] || target.kind}</p>
        <div className="flex justify-center mb-4">
          <QRCodeSVG value={url} size={256} />
        </div>
        <p className="text-center text-xs text-slate-500 mb-4 break-all">{url}</p>
        <div className="flex gap-2 print:hidden">
          <button onClick={onClose} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded font-semibold">Close</button>
          <button onClick={print} className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded font-bold flex items-center justify-center gap-2">
            <Printer className="w-4 h-4" />Print
          </button>
        </div>
      </div>
    </div>
  );
};

export default QRCodesPage;
